<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  tasks: {
    type: Array,
    required: true,
  },
  columns: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['view-task']);

const { t } = useI18n();

// Récupérer les agents
const agents = useMapGetter('agents/getVerifiedAgents');

// Trier les tâches par date de création (plus récent en premier)
const sortedTasks = computed(() => {
  return [...props.tasks].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
});

// Grouper les tâches par colonne
const groupedTasks = computed(() => {
  const groups = {};

  sortedTasks.value.forEach(task => {
    const columnId = task.status;
    if (!groups[columnId]) {
      const column = props.columns[columnId];
      groups[columnId] = {
        id: columnId,
        title: column?.title || columnId,
        color: column?.color,
        bgColor: column?.bgColor,
        tasks: [],
      };
    }
    groups[columnId].tasks.push(task);
  });

  // Trier les groupes selon l'ordre des colonnes
  return Object.values(groups).sort((a, b) => {
    const orderA = props.columns[a.id]?.order ?? 999;
    const orderB = props.columns[b.id]?.order ?? 999;
    return orderA - orderB;
  });
});

// Obtenir l'agent assigné
const getAssignedAgent = task => {
  if (!task.assignees || task.assignees.length === 0) return null;
  return agents.value.find(a => a.id === task.assignees[0]);
};

// Formater la date (style Linear - court)
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Réinitialiser les heures pour comparer seulement les dates
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const tomorrowOnly = new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate()
  );

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  }
  if (dateOnly.getTime() === tomorrowOnly.getTime()) {
    return 'Tomorrow';
  }

  // Format court: "Jan 15" ou "15 Jan" selon la locale
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

// Vérifier si la date est dépassée
const isOverdue = task => {
  if (!task.dueDate) return false;
  return new Date(task.dueDate) < new Date();
};

// Config priorité (style Linear - plus subtil)
const getPriorityConfig = priority => {
  switch (priority) {
    case 'high':
      return {
        color: 'text-red-700 dark:text-red-400',
        bg: 'bg-red-50/50 dark:bg-red-900/10',
        icon: 'i-lucide-chevrons-up',
      };
    case 'medium':
      return {
        color: 'text-orange-700 dark:text-orange-400',
        bg: 'bg-orange-50/50 dark:bg-orange-900/10',
        icon: 'i-lucide-chevron-up',
      };
    case 'low':
      return {
        color: 'text-blue-700 dark:text-blue-400',
        bg: 'bg-blue-50/50 dark:bg-blue-900/10',
        icon: 'i-lucide-chevron-down',
      };
    default:
      return {
        color: 'text-n-slate-10',
        bg: 'bg-n-alpha-1',
        icon: 'i-lucide-minus',
      };
  }
};

// Extraire texte plein du HTML
const getPlainText = html => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};
</script>

<template>
  <div class="w-full max-w-6xl mx-auto">
    <!-- Header de la liste - Style Linear -->
    <div
      class="grid grid-cols-10 gap-3 px-6 py-2.5 border-b border-n-weak/50 sticky top-0 bg-n-background z-10"
    >
      <div
        class="col-span-5 text-[11px] font-medium text-n-slate-10 uppercase tracking-wider"
      >
        {{ t('TASKS.LIST_VIEW.TITLE') }}
      </div>
      <div
        class="col-span-2 text-[11px] font-medium text-n-slate-10 uppercase tracking-wider"
      >
        {{ t('TASKS.LIST_VIEW.PRIORITY') }}
      </div>
      <div
        class="col-span-2 text-[11px] font-medium text-n-slate-10 uppercase tracking-wider"
      >
        {{ t('TASKS.LIST_VIEW.ASSIGNEE') }}
      </div>
      <div
        class="col-span-1 text-[11px] font-medium text-n-slate-10 uppercase tracking-wider text-right"
      >
        {{ t('TASKS.LIST_VIEW.DUE_DATE') }}
      </div>
    </div>

    <!-- Groupes de tâches par colonne -->
    <div class="space-y-6 mt-4">
      <div v-for="group in groupedTasks" :key="group.id" class="group-section">
        <!-- Header du groupe -->
        <div
          class="flex items-center gap-2 px-6 py-2 mb-1 sticky top-[41px] bg-n-background z-[9]"
        >
          <div
            class="size-2 rounded-full flex-shrink-0"
            :class="!group.bgColor?.startsWith('[') ? group.bgColor : ''"
            :style="
              group.bgColor?.startsWith('[')
                ? group.bgColor
                    .replace('[background-color:', 'background-color:')
                    .replace(']', '')
                : {}
            "
          />
          <h3
            class="text-[12px] font-semibold tracking-tight"
            :class="
              !group.color?.startsWith('[') ? group.color : 'text-n-slate-12'
            "
            :style="
              group.color?.startsWith('[')
                ? group.color.replace('[color:', 'color:').replace(']', '')
                : {}
            "
          >
            {{ group.title }}
          </h3>
          <span class="text-[11px] text-n-slate-9 font-medium">
            {{ group.tasks.length }}
          </span>
        </div>

        <!-- Tâches du groupe -->
        <div class="divide-y divide-n-weak/20">
          <div
            v-for="task in group.tasks"
            :key="task.id"
            class="grid grid-cols-10 gap-3 px-6 py-2.5 cursor-pointer group relative overflow-hidden transition-all duration-200 hover:bg-n-alpha-1/60"
            @click="emit('view-task', task)"
          >
            <!-- Barre latérale colorée au survol - animation raffinée -->
            <div
              class="absolute left-0 top-0 bottom-0 w-0.5 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-200 origin-center"
              :class="!group.bgColor?.startsWith('[') ? group.bgColor : ''"
              :style="
                group.bgColor?.startsWith('[')
                  ? group.bgColor
                      .replace('[background-color:', 'background-color:')
                      .replace(']', '')
                  : {}
              "
            />

            <!-- Effet de fond subtil au survol -->
            <div
              class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              :class="!group.bgColor?.startsWith('[') ? group.bgColor : ''"
              :style="
                group.bgColor?.startsWith('[')
                  ? `background: linear-gradient(90deg, ${group.bgColor.replace('[background-color:', '').replace(']', '')}10 0%, transparent 100%)`
                  : {}
              "
            />

            <!-- Titre et ID -->
            <div
              class="col-span-5 min-w-0 flex items-center gap-3 relative z-10"
            >
              <!-- ID de la tâche -->
              <span
                class="text-[11px] font-mono text-n-slate-9 flex-shrink-0 w-12 transition-colors group-hover:text-n-slate-11"
              >
                #{{ task.id }}
              </span>

              <!-- Titre avec description -->
              <div class="min-w-0 flex-1">
                <h4
                  class="text-[13px] font-medium text-n-slate-12 truncate leading-tight group-hover:text-n-brand transition-colors"
                >
                  {{ task.title }}
                </h4>
                <p
                  v-if="task.description"
                  class="text-[11px] text-n-slate-9 line-clamp-1 mt-0.5 leading-tight"
                >
                  {{ getPlainText(task.description) }}
                </p>
              </div>
            </div>

            <!-- Priorité -->
            <div class="col-span-2 flex items-center relative z-10">
              <div
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-all group-hover:shadow-sm"
                :class="getPriorityConfig(task.priority).bg"
              >
                <span
                  :class="[
                    getPriorityConfig(task.priority).icon,
                    getPriorityConfig(task.priority).color,
                  ]"
                  class="size-3 transition-transform group-hover:scale-110"
                />
                <span
                  :class="getPriorityConfig(task.priority).color"
                  class="text-[11px] font-medium"
                >
                  {{ t(`TASKS.PRIORITY.${task.priority.toUpperCase()}`) }}
                </span>
              </div>
            </div>

            <!-- Assigné -->
            <div class="col-span-2 flex items-center min-w-0 relative z-10">
              <div
                v-if="getAssignedAgent(task)"
                class="flex items-center gap-1.5 min-w-0"
              >
                <div
                  v-if="
                    getAssignedAgent(task).thumbnail ||
                    getAssignedAgent(task).avatar_url
                  "
                  class="size-5 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-n-weak/50 transition-transform group-hover:scale-110 group-hover:ring-2"
                >
                  <img
                    :src="
                      getAssignedAgent(task).thumbnail ||
                      getAssignedAgent(task).avatar_url
                    "
                    :alt="getAssignedAgent(task).name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <span
                  class="text-[12px] text-n-slate-11 truncate group-hover:text-n-slate-12 transition-colors"
                >
                  {{ getAssignedAgent(task).name }}
                </span>
              </div>
              <span v-else class="text-[11px] text-n-slate-9">—</span>
            </div>

            <!-- Date d'échéance -->
            <div class="col-span-1 flex items-center justify-end relative z-10">
              <div v-if="task.dueDate" class="flex items-center gap-1">
                <span
                  class="text-[11px] font-medium tabular-nums transition-colors"
                  :class="
                    isOverdue(task)
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-n-slate-10 group-hover:text-n-slate-11'
                  "
                >
                  {{ formatDate(task.dueDate) }}
                </span>
              </div>
              <span v-else class="text-[11px] text-n-slate-9">—</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- État vide -->
    <div
      v-if="groupedTasks.length === 0"
      class="flex flex-col items-center justify-center py-20 text-center"
    >
      <div
        class="size-12 rounded-full bg-n-alpha-1 flex items-center justify-center mb-3 border border-n-weak/50"
      >
        <span class="i-lucide-inbox size-6 text-n-slate-9" />
      </div>
      <p class="text-[13px] font-medium text-n-slate-12 mb-1">
        {{ t('TASKS.LIST.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-[11px] text-n-slate-10">
        {{ t('TASKS.LIST.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>
  </div>
</template>
